import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { cellId, type ArgumentGroup, type Round } from '@flow/core';
import { useUseCases } from '../../app/services';
import { ApiError } from '../../infra/api';
import { useAiAvailable, useSuggestTitle } from './useAi';
import { createElement } from 'react';
import { Copy } from '../../ui/Brand';

const MIN_CHARS = 8;
const SETTLE_MS = 1500;

/**
 * While signed in: name each untitled argument shortly after its text stops changing, unless you're still typing
 * in that exact box. Typing elsewhere doesn't delay it. One request at a time; each (argument, text) is tried once,
 * so failures don't loop. Never replaces your own titles.
 */
export const useAutoTitles = (round: Round, groups: ArgumentGroup[], titles: Map<string, string>, editingCellId: string | null) => {
  const available = useAiAvailable();
  const suggest = useSuggestTitle();
  const uc = useUseCases();
  const tried = useRef(new Set<string>());
  const warned = useRef(false);
  // State, not a ref: when one request finishes, the next candidate gets its turn.
  const [busy, setBusy] = useState(false);

  const next = available
    ? groups.find((g) => !titles.has(g.headRowId) && g.text.length >= MIN_CHARS && editingCellId !== cellId(g.headRowId, g.caseSegId) && !tried.current.has(`${g.headRowId}:${g.text}`))
    : undefined;
  // A string key: the effect restarts only when the candidate itself changes, not on every unrelated save.
  const key = next ? `${next.headRowId}:${next.text}` : null;
  const latest = useRef({ round, suggest, uc, next });
  latest.current = { round, suggest, uc, next };

  useEffect(() => {
    if (!key || busy) return;
    const id = setTimeout(async () => {
      const { round: r, suggest: s, uc: u, next: g } = latest.current;
      if (!g) return;
      tried.current.add(key);
      setBusy(true);
      try {
        const title = await s(r, g.text);
        if (title) await u.setArgumentTitle(r.id, g.headRowId, title, 'ai');
      } catch (e) {
        if (!warned.current) {
          warned.current = true;
          const why = e instanceof ApiError && e.body && typeof e.body === 'object' && 'message' in e.body ? String(e.body.message) : "{flow}'s AI service didn't answer.";
          notifications.show({ color: 'yellow', title: 'Argument titles are paused', message: createElement(Copy, null, `${why} You can still name arguments yourself (tag button).`) });
        }
      } finally {
        setBusy(false);
      }
    }, SETTLE_MS);
    return () => clearTimeout(id);
  }, [key, busy]);
};
